import { Params, Router } from '@angular/router';

const RETURN_URL_STORAGE_KEY = 'psc.returnUrl';
const RETURN_URL_TTL_MS = 30 * 60 * 1000;
const MAX_RETURN_URL_LENGTH = 2048;
const DEFAULT_RETURN_URL = '/';
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;
const AUTH_PATH_PATTERN = /^\/auth(?:[/?#;(]|$)/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function hasSafeShape(url: string): boolean {
  return (
    url.startsWith('/') &&
    !url.startsWith('//') &&
    !url.includes('\\') &&
    !CONTROL_CHARACTER_PATTERN.test(url) &&
    !AUTH_PATH_PATTERN.test(url)
  );
}

function decodedOrNull(url: string): string | null {
  try {
    return decodeURIComponent(url);
  } catch {
    return null;
  }
}

export function isSafeReturnUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.length > MAX_RETURN_URL_LENGTH) {
    return false;
  }
  const decoded = decodedOrNull(url);
  return decoded !== null && hasSafeShape(url) && hasSafeShape(decoded);
}

function storedReturnUrl(): string | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(RETURN_URL_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(stored)) {
    return null;
  }
  const { url, expiresAt } = stored;
  return typeof expiresAt === 'number' &&
    expiresAt > Date.now() &&
    isSafeReturnUrl(url)
    ? url
    : null;
}

function forgetReturnUrl(): void {
  try {
    localStorage.removeItem(RETURN_URL_STORAGE_KEY);
  } catch {
    return;
  }
}

export function rememberReturnUrl(url: string | null): void {
  if (!isSafeReturnUrl(url) || url === DEFAULT_RETURN_URL) {
    forgetReturnUrl();
    return;
  }
  try {
    localStorage.setItem(
      RETURN_URL_STORAGE_KEY,
      JSON.stringify({ url, expiresAt: Date.now() + RETURN_URL_TTL_MS }),
    );
  } catch {
    return;
  }
}

export function returnUrlQueryParams(url: string): Params {
  return isSafeReturnUrl(url) && url !== DEFAULT_RETURN_URL
    ? { returnUrl: url }
    : {};
}

function navigateOrHome(router: Router, url: string): Promise<boolean> {
  return router
    .navigateByUrl(url)
    .catch(() => router.navigateByUrl(DEFAULT_RETURN_URL));
}

export function navigateToReturnUrl(
  router: Router,
  returnUrl: string | null,
): Promise<boolean> {
  forgetReturnUrl();
  return navigateOrHome(
    router,
    isSafeReturnUrl(returnUrl) ? returnUrl : DEFAULT_RETURN_URL,
  );
}

export function navigateToStoredReturnUrl(router: Router): Promise<boolean> {
  const stored = storedReturnUrl();
  forgetReturnUrl();
  return navigateOrHome(router, stored ?? DEFAULT_RETURN_URL);
}
